import React, { useEffect, useMemo, useState } from "react";
import MDEditor, { commands } from "@uiw/react-md-editor";
import type {
  ICommand,
  TextAreaTextApi,
  TextState,
} from "@uiw/react-md-editor";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface AddNoteModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (note: {
    content: string;
    plainText: string;
  }) => Promise<void> | void;
  isSaving?: boolean;
}

const MAX_NOTE_LENGTH = 280;

const underlineCommand: ICommand = {
  name: "underline",
  keyCommand: "underline",
  buttonProps: {
    "aria-label": "Insert underline",
    title: "Insert underline",
  },
  icon: <span className="text-sm font-semibold leading-none underline">U</span>,
  execute: (state: TextState, api: TextAreaTextApi) => {
    if (state.selectedText) {
      api.replaceSelection(`++${state.selectedText}++`);
      return;
    }

    api.replaceSelection("++++");
    api.setSelectionRange({
      start: state.selection.start + 2,
      end: state.selection.end + 2,
    });
  },
};

const parseMarkdownToken = (
  input: string,
  startIndex: number,
): { value: string; nextIndex: number } | null => {
  const currentChar = input[startIndex];
  const isImage = currentChar === "!" && input[startIndex + 1] === "[";
  const isLink = currentChar === "[";

  if (!isImage && !isLink) return null;

  const textStart = startIndex + (isImage ? 2 : 1);
  const textEnd = input.indexOf("]", textStart);

  if (textEnd === -1 || input[textEnd + 1] !== "(") return null;

  const urlEnd = input.indexOf(")", textEnd + 2);

  if (urlEnd === -1) return null;

  return {
    value: isImage ? " " : input.slice(textStart, textEnd),
    nextIndex: urlEnd + 1,
  };
};

const stripMarkdownLinksAndImages = (input: string): string => {
  let result = "";
  let i = 0;

  while (i < input.length) {
    const token = parseMarkdownToken(input, i);

    if (token) {
      result += token.value;
      i = token.nextIndex;
      continue;
    }

    result += input[i];
    i += 1;
  }

  return result;
};

const getPlainText = (markdown: string): string =>
  stripMarkdownLinksAndImages(markdown)
    .replace(/[*_~`>#+-]/g, " ")
    .replace(/\n+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const AddNoteModal = ({
  open,
  onOpenChange,
  onSave,
  isSaving = false,
}: AddNoteModalProps) => {
  const { t } = useTranslation();
  const [value, setValue] = useState("");

  useEffect(() => {
    if (!open) {
      setValue("");
    }
  }, [open]);

  const plainText = useMemo(() => getPlainText(value), [value]);
  const isOverLimit = plainText.length > MAX_NOTE_LENGTH;
  const isEmpty = plainText.length === 0;

  const handleChange = (nextValue?: string) => {
    setValue(nextValue || "");
  };

  const handleSave = async () => {
    if (isEmpty || isOverLimit) return;

    await onSave({ content: value.trim(), plainText });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[500px] w-[min(1100px,calc(100vw-3rem))] max-w-none flex-col overflow-hidden p-0">
        <DialogHeader className="border-b p-6">
          <DialogTitle>{t("chats.addInternalNoteTitle")}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden bg-white p-5">
          <div className="relative h-full" data-color-mode="light">
            <div className="chat-note-editor relative h-full overflow-hidden rounded-[24px] border border-slate-200 bg-white">
              <div className="h-full">
                <MDEditor
                  className="h-full"
                  value={value}
                  onChange={handleChange}
                  preview="edit"
                  highlightEnable={false}
                  visibleDragbar={false}
                  textareaProps={{
                    placeholder: t("chats.notePlaceholder"),
                    maxLength: 1200,
                  }}
                  height="100%"
                  minHeight={200}
                  commands={[
                    commands.bold,
                    commands.italic,
                    commands.strikethrough,
                    underlineCommand,
                    commands.divider,
                    commands.link,
                    commands.divider,
                    commands.unorderedListCommand,
                    commands.orderedListCommand,
                  ]}
                  extraCommands={[]}
                />
              </div>
              <div
                className={`absolute right-6 bottom-4 rounded-full border bg-white px-3 py-1 text-sm font-medium shadow-sm ${
                  isOverLimit
                    ? "border-red-200 bg-red-50 text-red-500"
                    : "border-slate-200 bg-white text-slate-500"
                }`}
              >{`${plainText.length}/${MAX_NOTE_LENGTH}`}</div>
            </div>
          </div>
        </div>

        <DialogFooter className="items-center justify-between border-t p-4 sm:justify-between">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-slate-500"
          >
            {t("chats.cancel")}
          </Button>
          <Button
            onClick={handleSave}
            disabled={isEmpty || isOverLimit || isSaving}
          >
            {t("chats.saveNote")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
